import type { InputFrame } from "../input";
import { groundKind, halfX, halfZ, surfaceAt, wallNormal, EDGE_FAR, type EdgeSide, type Level } from "../level";
import type { Skater } from "../skater";
import { tagsUnder } from "../graffiti";
import { pushProps, stepProps, type PropBody } from "../props";
import { aimTarget, targetHit } from "../targets";
import type { SkaterEvent } from "./state";
import {
  PAINT_AIM_VY,
  PAINT_COOLDOWN,
  PAINT_G,
  PAINT_LIFE,
  PAINT_LOFT,
  PAINT_MUZZLE_FWD,
  PAINT_MUZZLE_Y,
  PAINT_SPEED,
  TAG_WASH_TIME,
  WASH_FWD,
  WASH_PITCH,
  WASH_REACH,
  WASH_Y,
} from "./tuning";

// The backpack (#136): on foot you carry one item out of a pack on your
// back, L1 / R1 (the fast spins, free on foot) switching to the previous or
// next. Stepping back on the board puts it away, and the pack remembers it:
// it's what comes out next time you step off. The first item past the board
// is a paintball gun: □ (the flip button, since ✕ still jumps and climbs)
// fires a paintball the way you face, in an arc under real gravity. A hit
// on the park (floor, ramp, box, ledge or the fence) is a `splat` event
// with where and which way the surface faces; the renderer leaves a splat
// there (`paint-splats.ts`). Balls fly on the sim's clock through the
// sim's own heightfield, so a run stays deterministic. The BMX (#141) is a
// ride, like the board: step on with it out and you ride it (`sim/rides.ts`);
// so are the scooter (#139) and the skates (#140). The power washer (#151)
// is held like the gun: hold □ and a jet sprays ahead and a little down,
// a `wash` event each step where it hits the park, and the paint there
// fades away (`SplatPool.wash`). A map's graffiti tags (`graffiti.ts`) get
// scrubbed while the jet hits them, and are clean after `TAG_WASH_TIME` of it.
// The jet knocks a map's cones and cans about (`props.ts`), and sprayed
// standing on a slope it shoves you back a little (`sim/foot.ts`).

export type ItemId = "board" | "paintball" | "scooter" | "washer" | "skates" | "bmx";

/** What's in the pack, in the order L1 / R1 go through them. A new item is one entry here plus what it does on foot. */
export const ITEMS: readonly { id: ItemId; name: string }[] = [
  { id: "board", name: "Board" },
  { id: "paintball", name: "Paintball gun" },
  // The scooter (#139) goes before the bike, so L1 from the board is still the bike.
  { id: "scooter", name: "Scooter" },
  // The power washer (#151) between the scooter and the skates, so R1 twice is still the scooter and L1 twice the skates.
  { id: "washer", name: "Power washer" },
  // The skates (#140) after it, still before the bike.
  { id: "skates", name: "Roller skates" },
  { id: "bmx", name: "BMX bike" },
];

/** Splat colours, a shot each in turn. */
export const PAINT_COLORS: readonly number[] = [0xff3b6b, 0x2fd0ff, 0xffd23f, 0x7bea4a, 0xb36bff, 0xff8a2a];

export interface PaintBall {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Seconds in flight. */
  t: number;
  color: number;
}

/** Where a paintball hit: the spot, the surface's outward normal, and the ball's colour. */
export interface PaintHit {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
  color: number;
}

export interface PackState {
  /** The item out on foot, and the one that comes out next time you step off. */
  item: ItemId;
  /** Seconds until the gun can fire again. */
  cooldown: number;
  /** Shots fired, which picks each one's colour. */
  shots: number;
  /** Paintballs in flight. */
  balls: PaintBall[];
  /** The map's targets (#137) burst so far, by index into `Level.targets`: each counts once and stays down. */
  burst: Set<number>;
  /** The power washer's jet (#151) this step, while it sprays; null otherwise. */
  jet: Jet | null;
  /** How far each of the map's graffiti tags (#151) is scrubbed, 0 to 1, by index into `Level.graffiti` (missing is 0). */
  scrub: number[];
  /** The tags washed clean: each counts once and stays clean. */
  cleaned: Set<number>;
  /** The map's cones and cans (#151, `props.ts`), by index into `Level.props`: where the jet's knocked them. */
  props: PropBody[];
}

/** Where the power washer's jet hits the park (#151): the spot and the surface's outward normal. */
export interface WashHit {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
}

/** The power washer's jet: from the nozzle to where it hits, or out to its reach (`hit` null) if it hits nothing. */
export interface Jet {
  x0: number;
  y0: number;
  z0: number;
  x1: number;
  y1: number;
  z1: number;
  hit: WashHit | null;
}

export function newPack(): PackState {
  return { item: "board", cooldown: 0, shots: 0, balls: [], burst: new Set(), jet: null, scrub: [], cleaned: new Set(), props: [] };
}

/** The item in your hands: the pack's item while on foot, nothing on the board. */
export function heldItem(sk: Skater): ItemId | null {
  return sk.mode === "foot" && sk.foot ? sk.pack.item : null;
}

/** The item walked beside you (#145): what's out of the pack on foot, unless you're up on a ledge. */
export function walkingWith(sk: Skater): ItemId | null {
  return sk.mode === "foot" && !sk.foot?.ledge ? heldItem(sk) : null;
}

/** The pack's item `by` places along from `from`, wrapping round. */
export function cycleItem(from: ItemId, by: number): ItemId {
  const i = ITEMS.findIndex((it) => it.id === from);
  const n = ITEMS.length;
  return ITEMS[(((i + by) % n) + n) % n]!.id;
}

/**
 * The pack's step, after the mode's: on foot (not hanging off a ledge) L1 /
 * R1 switch items, □ fires the gun and held sprays the washer; paintballs
 * in flight move on and splat whatever mode you're in.
 */
export function stepPack(sk: Skater, input: InputFrame, dt: number, events: SkaterEvent[]): void {
  const p = sk.pack;
  p.cooldown = Math.max(0, p.cooldown - dt);
  const foot = sk.foot;
  p.jet = null;
  if (sk.mode === "foot" && foot && !foot.ledge) {
    const by = (input.pressed.spinR ? 1 : 0) - (input.pressed.spinL ? 1 : 0);
    if (by !== 0) {
      p.item = cycleItem(p.item, by);
      events.push({ type: "item", item: p.item });
    }
    if (p.item === "paintball" && input.pressed.flip && p.cooldown === 0) fire(sk, foot.speed, events);
    if (p.item === "washer" && input.held.flip) {
      p.jet = aimJet(sk);
      const j = p.jet;
      for (const index of pushProps(p.props, j.x0, j.y0, j.z0, j.x1, j.y1, j.z1, dt)) events.push({ type: "knock", index });
      if (p.jet.hit) {
        events.push({ type: "wash", hit: p.jet.hit });
        scrubTags(sk.level, p, p.jet.hit, dt, events);
      }
    }
  }
  if (p.balls.length > 0) stepBalls(sk.level, p, dt, events);
  if (p.props.length > 0) stepProps(sk.level, p.props, dt);
}

/** Scrubs the graffiti tags the jet hits this step; one scrubbed for `TAG_WASH_TIME` in all is clean. */
function scrubTags(level: Level, p: PackState, hit: WashHit, dt: number, events: SkaterEvent[]): void {
  const tags = level.graffiti;
  if (!tags) return;
  for (const i of tagsUnder(tags, p.cleaned, hit.x, hit.y, hit.z)) {
    const s = Math.min(1, (p.scrub[i] ?? 0) + dt / TAG_WASH_TIME);
    p.scrub[i] = s;
    // A hair under 1 counts: the steps' dt don't add up exactly.
    if (s < 1 - 1e-6) continue;
    p.cleaned.add(i);
    events.push({ type: "clean", index: i, cleaned: p.cleaned.size, of: tags.length });
  }
}

/** Fires a paintball from the muzzle, carried on by your walking pace; a muzzle already in a wall splats it there. */
function fire(sk: Skater, pace: number, events: SkaterEvent[]): void {
  const p = sk.pack;
  const color = PAINT_COLORS[p.shots % PAINT_COLORS.length]!;
  p.shots++;
  p.cooldown = PAINT_COOLDOWN;
  events.push({ type: "shot" });
  const shot = launch(sk, pace, color);
  if ("hit" in shot) events.push({ type: "splat", hit: shot.hit });
  else p.balls.push(shot.ball);
}

/**
 * A shot leaving the muzzle now: the ball, lofted the way you face (or
 * pulled onto a target by the auto-aim, `aim`), or the splat if the muzzle
 * is already in a wall.
 */
function launch(sk: Skater, pace: number, color: number): { ball: PaintBall; aim: number | null } | { hit: PaintHit } {
  const p = sk.pack;
  const f = sk.forward;
  const y = sk.y + PAINT_MUZZLE_Y;
  const x = sk.x + f.x * PAINT_MUZZLE_FWD;
  const z = sk.z + f.z * PAINT_MUZZLE_FWD;
  const hit = paintHit(sk.level, sk.x, y, sk.z, x, y, z, color);
  if (hit) return { hit };
  const flat = PAINT_SPEED * Math.cos(PAINT_LOFT) + pace;
  let dirX = f.x;
  let dirZ = f.z;
  let vy = PAINT_SPEED * Math.sin(PAINT_LOFT);
  // A little auto-aim (#137): a target just off the way you face pulls the shot onto it, lofted to arrive at its middle.
  const targets = sk.level.targets;
  const aim = targets ? aimTarget(targets, p.burst, x, z, sk.yaw) : null;
  if (targets && aim !== null) {
    const tg = targets[aim]!;
    const dx = tg.x - x;
    const dz = tg.z - z;
    const d = Math.hypot(dx, dz);
    dirX = dx / d;
    dirZ = dz / d;
    const t = d / flat;
    vy = Math.max(-PAINT_AIM_VY, Math.min(PAINT_AIM_VY, (tg.y - y + 0.5 * PAINT_G * t * t) / t));
  }
  return { ball: { x, y, z, vx: dirX * flat, vy, vz: dirZ * flat, t: 0, color }, aim };
}

/** What a ball's step did: burst a target (`t` of the way along the step), splatted, flew off, or flies on (null). */
type BallStep = { target: number; t: number } | { hit: PaintHit } | "gone" | null;

/** Moves a ball on `dt` under gravity, into a target or the park. Leaves `b` where it was if it hit something. */
function stepBall(level: Level, burst: ReadonlySet<number>, b: PaintBall, dt: number): BallStep {
  b.t += dt;
  b.vy -= PAINT_G * dt;
  const x = b.x + b.vx * dt;
  const y = b.y + b.vy * dt;
  const z = b.z + b.vz * dt;
  const tg = level.targets ? targetHit(level.targets, burst, b.x, b.y, b.z, x, y, z) : null;
  if (tg) return { target: tg.index, t: tg.t };
  const hit = paintHit(level, b.x, b.y, b.z, x, y, z, b.color);
  if (hit) return { hit };
  b.x = x;
  b.y = y;
  b.z = z;
  return b.t > PAINT_LIFE || gone(level, b) ? "gone" : null;
}

function stepBalls(level: Level, p: PackState, dt: number, events: SkaterEvent[]): void {
  let keep = 0;
  for (const b of p.balls) {
    const r = stepBall(level, p.burst, b, dt);
    if (r === "gone") continue;
    if (r && "target" in r) {
      p.burst.add(r.target);
      events.push({ type: "target", index: r.target, burst: p.burst.size, of: level.targets!.length });
      continue;
    }
    if (r) {
      events.push({ type: "splat", hit: r.hit });
      continue;
    }
    p.balls[keep++] = b;
  }
  p.balls.length = keep;
}

/** Where the next shot will land (#143): the spot, the surface's outward normal, and the target the auto-aim has, if any. */
export interface ShotAim {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
  /** The target the auto-aim pulls the shot onto (index into `Level.targets`), or null. */
  aim: number | null;
  /** The target the shot bursts, or null if it lands on the park. */
  target: number | null;
}

/**
 * Flies the shot you'd fire now (same muzzle, pace, auto-aim and gravity,
 * stepped `dt` at a time like the sim's balls) without firing it, and
 * returns where it lands, or null if it flies off or into the sea. For the
 * crosshair; it changes nothing.
 */
export function predictShot(sk: Skater, dt: number): ShotAim | null {
  const pace = sk.foot?.speed ?? 0;
  const shot = launch(sk, pace, 0);
  if ("hit" in shot) return { ...normalOf(shot.hit), aim: null, target: null };
  const b = shot.ball;
  const level = sk.level;
  for (;;) {
    const x0 = b.x;
    const y0 = b.y;
    const z0 = b.z;
    const r = stepBall(level, sk.pack.burst, b, dt);
    if (r === "gone") return null;
    if (!r) continue;
    if ("hit" in r) return { ...normalOf(r.hit), aim: shot.aim, target: null };
    // Into a target: the spot on its ball, facing back along the shot.
    const x = x0 + b.vx * dt * r.t;
    const y = y0 + b.vy * dt * r.t;
    const z = z0 + b.vz * dt * r.t;
    const tg = level.targets![r.target]!;
    const n = Math.hypot(x - tg.x, y - tg.y, z - tg.z) || 1;
    return { x, y, z, nx: (x - tg.x) / n, ny: (y - tg.y) / n, nz: (z - tg.z) / n, aim: shot.aim, target: r.target };
  }
}

/**
 * The power washer's jet (#151) if it sprayed now: straight from the
 * nozzle, the way you face tipped down by `WASH_PITCH`, to the first thing
 * it hits within `WASH_REACH` (a nozzle already in a wall hits that). For
 * the sim each step it sprays, and the crosshair; it changes nothing.
 */
export function aimJet(sk: Skater): Jet {
  const f = sk.forward;
  const y0 = sk.y + WASH_Y;
  const x0 = sk.x + f.x * WASH_FWD;
  const z0 = sk.z + f.z * WASH_FWD;
  const flat = WASH_REACH * Math.cos(WASH_PITCH);
  const x1 = x0 + f.x * flat;
  const y1 = y0 - WASH_REACH * Math.sin(WASH_PITCH);
  const z1 = z0 + f.z * flat;
  const h = paintHit(sk.level, sk.x, y0, sk.z, x0, y0, z0, 0) ?? paintHit(sk.level, x0, y0, z0, x1, y1, z1, 0);
  if (!h) return { x0, y0, z0, x1, y1, z1, hit: null };
  const hit = normalOf(h);
  return { x0, y0, z0, x1: hit.x, y1: hit.y, z1: hit.z, hit };
}

const normalOf = (h: PaintHit) => ({ x: h.x, y: h.y, z: h.z, nx: h.nx, ny: h.ny, nz: h.nz });

/** Far out past a soft side, or fallen through the floor. */
function gone(level: Level, b: PaintBall): boolean {
  return Math.abs(b.x) > halfX(level) + EDGE_FAR || Math.abs(b.z) > halfZ(level) + EDGE_FAR || b.y < -5;
}

const soft = (level: Level, side: EdgeSide): boolean => level.edges?.some((e) => e.side === side) ?? false;

/** A step in the heightfield taller than this, between two points a few cm apart, is a box's side, not its top (m). */
const SIDE_STEP = 0.1;

/**
 * Whether a ball going from (x0, y0, z0) to (x1, y1, z1) hits the park: the
 * fence on a walled side, or the heightfield (anything the skater rides,
 * from the floor to a box's side). The contact is found by halving the
 * step; a box's side faces the way `wallNormal` says (toward open ground),
 * anything else the way its slope does. Rails don't stop paintballs, and
 * the sea swallows them without a splat (null).
 */
export function paintHit(
  level: Level,
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  color: number,
): PaintHit | null {
  const hx = halfX(level);
  const hz = halfZ(level);
  // The fence, where there's a wall: the first fence line the step crosses.
  let fence = 2;
  let fnx = 0;
  let fnz = 0;
  const cross = (a0: number, a1: number, lim: number, side: EdgeSide, sign: number, isX: boolean) => {
    if (sign * a1 <= lim || sign * a0 > lim || soft(level, side)) return;
    const t = (lim - sign * a0) / (sign * (a1 - a0));
    if (t < fence) {
      fence = t;
      fnx = isX ? -sign : 0;
      fnz = isX ? 0 : -sign;
    }
  };
  cross(x0, x1, hx, "east", 1, true);
  cross(x0, x1, hx, "west", -1, true);
  cross(z0, z1, hz, "south", 1, false);
  cross(z0, z1, hz, "north", -1, false);

  const inside = (t: number) => {
    const x = x0 + (x1 - x0) * t;
    const z = z0 + (z1 - z0) * t;
    return surfaceAt(level, x, z).h >= y0 + (y1 - y0) * t;
  };
  const end = Math.min(1, fence);
  if (inside(end)) {
    // Somewhere before `end` it went in: halve down to the contact.
    let lo = 0;
    let hi = end;
    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      if (inside(mid)) hi = mid;
      else lo = mid;
    }
    const xo = x0 + (x1 - x0) * lo;
    const zo = z0 + (z1 - z0) * lo;
    const xi = x0 + (x1 - x0) * hi;
    const zi = z0 + (z1 - z0) * hi;
    if (level.edges && groundKind(level, xi, zi) === "water") return null;
    const sIn = surfaceAt(level, xi, zi);
    const y = y0 + (y1 - y0) * hi;
    if (sIn.h - surfaceAt(level, xo, zo).h > SIDE_STEP) {
      const n = wallNormal(level, xi, zi, xo, zo);
      return { x: xo, y, z: zo, nx: n.x, ny: 0, nz: n.z, color };
    }
    const l = Math.hypot(sIn.gx, 1, sIn.gz);
    return { x: xi, y: sIn.h, z: zi, nx: -sIn.gx / l, ny: 1 / l, nz: -sIn.gz / l, color };
  }
  if (fence <= 1) {
    return {
      x: x0 + (x1 - x0) * fence,
      y: y0 + (y1 - y0) * fence,
      z: z0 + (z1 - z0) * fence,
      nx: fnx,
      ny: 0,
      nz: fnz,
      color,
    };
  }
  return null;
}
